<script setup lang="ts">
import Box from '@uiKit/elements/Box.vue'
import Text from '@uiKit/elements/Text.vue'
import PiIcon, { type PiIconName } from '@uiKit/elements/PiIcon.vue'

interface Pillar {
  icon: PiIconName
  title: string
  body: string
}

const pillars: Pillar[] = [
  {
    icon: 'shield',
    title: 'VARA Regulated',
    body: 'Sliceal is licensed by the Virtual Assets Regulatory Authority of Dubai.',
  },
  {
    icon: 'building',
    title: 'DLD Verified',
    body: 'Every property is registered with the Dubai Land Department.',
  },
  {
    icon: 'lock',
    title: 'Escrow Protected',
    body: 'Your funds are held by a UAE bank in escrow — never on our balance sheet.',
  },
]
</script>

<template>
  <Box class="trust-panel" direction="col">
    <!-- Brand hero — a composed secure-verification motif rather than a stock
         skyline photo (see ux-principles §11: trust cues over hero imagery at
         the onboarding layer). Frames the flow the investor is about to start. -->
    <Box class="trust-panel__hero" direction="col" align="center" justify="center" gap="2">
      <Box class="trust-panel__hero-badge" align="center" justify="center">
        <PiIcon name="shield" class="trust-panel__hero-glyph" />
      </Box>
      <Text type="caption-sm" color="inverse" transform="uppercase" weight="semibold">
        Secure verification
      </Text>
    </Box>

    <Box class="trust-panel__body" direction="col">
      <Box class="trust-panel__top" direction="col" gap="4">
        <Box direction="col" gap="2">
          <Text type="micro" color="primary">WHY WE ASK</Text>
          <Text is="h2" type="title-lg" weight="bold" class="trust-panel__headline">
            Sliceal is regulated infrastructure for UAE fractional real-estate.
          </Text>
          <Text type="body" color="muted">
            These checks aren't ours — they're required by UAE financial regulators before you can
            invest. Most investors complete them in under five minutes.
          </Text>
        </Box>

        <Box direction="col" gap="3">
          <Box v-for="pillar in pillars" :key="pillar.title" class="trust-pillar" gap="3">
            <Box class="trust-pillar__icon" align="center" justify="center">
              <PiIcon :name="pillar.icon" class="trust-pillar__icon-glyph" />
            </Box>
            <Box direction="col" gap="1">
              <Text type="body" weight="semibold">{{ pillar.title }}</Text>
              <Text type="body-sm" color="muted">{{ pillar.body }}</Text>
            </Box>
          </Box>
        </Box>
      </Box>

      <Box class="trust-panel__partner" direction="col" align="center" gap="3">
        <Box class="trust-panel__partner-logo" align="center" justify="center">
          <PiIcon name="verified" class="trust-panel__partner-glyph" />
        </Box>
        <Box direction="col" align="center" gap="1">
          <Text type="body-sm" weight="semibold" align="center">Powered by Sumsub</Text>
          <Text type="caption" color="muted" align="center">VARA-licensed KYC partner</Text>
        </Box>
        <Text type="caption" color="muted" align="center" class="trust-panel__footnote">
          Documents are encrypted in transit and stored by Sumsub. We never store copies on Sliceal
          servers.
        </Text>
      </Box>
    </Box>
  </Box>
</template>

<style scoped>
.trust-panel {
  width: 100%;
  max-width: 440px;
  height: 100%;
  border-radius: var(--p-border-radius-lg);
  background: var(--p-surface-0);
  border: 1px solid var(--p-content-border-color);
  box-shadow:
    0 1px 2px rgba(16, 24, 40, 0.04),
    0 12px 24px rgba(16, 24, 40, 0.06);
  box-sizing: border-box;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.trust-panel__hero {
  height: 132px;
  flex-shrink: 0;
  background:
    radial-gradient(circle at 50% 0%, rgba(255, 255, 255, 0.22), transparent 60%),
    linear-gradient(135deg, var(--p-primary-600), var(--p-primary-400));
  padding: 1rem;
  box-sizing: border-box;
}

.trust-panel__hero-badge {
  width: 56px;
  height: 56px;
  border-radius: var(--p-border-radius-lg);
  background: rgba(255, 255, 255, 0.16);
  border: 1px solid rgba(255, 255, 255, 0.28);
}

.trust-panel__hero-glyph {
  font-size: 1.5rem;
  color: var(--p-surface-0);
}

.trust-panel__body {
  flex: 1;
  display: flex;
  flex-direction: column;
  padding: 1.5rem 1.5rem 1.75rem;
  gap: 1.75rem;
  min-height: 0;
}

.trust-panel__top {
  flex: 0 0 auto;
}

.trust-panel__headline {
  line-height: 1.25;
}

.trust-pillar {
  display: flex;
  align-items: flex-start;
}

.trust-pillar__icon {
  width: 40px;
  height: 40px;
  border-radius: var(--p-border-radius-md);
  background: var(--p-primary-50);
  color: var(--p-primary-700);
  flex-shrink: 0;
}

.trust-pillar__icon-glyph {
  font-size: 1.25rem;
  font-weight: 700;
}

.trust-panel__partner {
  margin-top: auto;
  padding: 1.25rem 0 0;
  border-top: 1px solid var(--p-content-border-color);
}

.trust-panel__partner-logo {
  width: 44px;
  height: 44px;
  border-radius: var(--p-border-radius-md);
  background: var(--p-primary-50);
  flex-shrink: 0;
  box-sizing: border-box;
}

.trust-panel__partner-glyph {
  font-size: 1.375rem;
  color: var(--p-primary-700);
}

.trust-panel__footnote {
  max-width: 320px;
  margin-top: 0.5rem;
}
</style>
